import { useLocale } from '@e2m-customer-portal/hooks';
import { Validation } from '@e2m-customer-portal/shared';
import { Popover } from '@e2m-customer-portal/ui';
import clsx from 'clsx';
import { addDays, addMinutes, format, Interval, isBefore, isWeekend, isWithinInterval } from 'date-fns';
import { UseInputFieldRules } from 'hooks/use-input-field-rules/use-input-field-rules';
import { equals } from 'ramda';
import { Fragment, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Direction } from '../../../../../domains/forecast/utils';
import {
  Device,
  ForecastProperty,
  ForecastSlice,
  ForecastTarget,
  MarketLocation,
  Maybe,
  Resolution,
  SpotPriceForecast,
} from '../../../../../generated/graphql';
import { SelectedCell } from '../ForecastList';
import { SpotPriceResult } from '../spot-price';
import { SelectHandle } from './DeviceTable';
import { DST_CORRECTION_ID } from './DeviceTableBody';
import { DecoratedCell } from './forecast-cell/DecoratedCell';
import { DSTMultiCell } from './forecast-cell/dst-adjustment/DSTMultiCell';

interface DeviceTableRowProps {
  marketLocation: MarketLocation;
  device: Device;
  interval: Interval;
  rowSlices: ForecastSlice[];
  date: Date;
  deadlineDate?: Date;
  rowIndex: number;
  spotPriceForecast?: SpotPriceForecast;
  showSpotPrices?: boolean;
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;
  readOnly?: boolean;
  resetTrigger: boolean;
  editedSlices: Set<string>;
  isHighlighted: boolean;
  overviewMap: Map<string, (HTMLInputElement | null)[][]>;
  selectHandle: SelectHandle;
  selectedCells: SelectedCell[];
  dstSliceID?: string;
  dstSliceValues?: Map<string, Array<Array<{ slice: ForecastSlice; value: string }>>>;
  powerLevelRules?: Validation<string | number>;
  rules?: UseInputFieldRules;
  setRevokeDowntimeInterval: React.Dispatch<React.SetStateAction<Interval | null>>;
  setEditedSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setErroneousSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setIsHighlighted: React.Dispatch<React.SetStateAction<boolean>>;
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, Array<Array<HTMLInputElement | null>>>>>;
  setSelectHandle: React.Dispatch<React.SetStateAction<SelectHandle>>;
  setSelectedCells: React.Dispatch<React.SetStateAction<SelectedCell[]>>;
  handleSelectAll: () => void;
  pasteCopiedCells: (event: React.KeyboardEvent<HTMLInputElement>, startRow: number, startColumn: number) => void;
  saveChanges: () => void;
  switchFocus: (sourceRow: number, sourceColumn: number, direction?: Direction) => boolean;
  selectRow: (index: number) => (event: React.MouseEvent<Element, MouseEvent>) => void;
  expandRowSelection: (index: number) => (event: React.MouseEvent<Element, MouseEvent>) => void;
  currentForecastProperty?: ForecastProperty;
  toggleDSTModal: () => void;
}

function DeviceTableRowMemo({
  marketLocation,
  device,
  interval,
  rowSlices,
  date,
  deadlineDate,
  rowIndex: row,
  spotPriceForecast,
  showSpotPrices,
  spotPrice,
  readOnly,
  resetTrigger,
  editedSlices,
  isHighlighted,
  overviewMap,
  selectHandle,
  selectedCells,
  dstSliceID,
  dstSliceValues,
  powerLevelRules,
  rules,
  setRevokeDowntimeInterval,
  setEditedSlices,
  setErroneousSlices,
  setIsHighlighted,
  setOverviewMap,
  setSelectHandle,
  setSelectedCells,
  handleSelectAll,
  pasteCopiedCells,
  saveChanges,
  switchFocus,
  selectRow,
  expandRowSelection,
  currentForecastProperty,
  toggleDSTModal,
}: Readonly<DeviceTableRowProps>) {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const [dstRef, setDstRef] = useState<HTMLInputElement | null>();
  const isReadOnly = (sliceDate: Date) =>
    (marketLocation.marketingStart != null && isBefore(sliceDate, marketLocation.marketingStart)) ||
    (marketLocation.marketingEnd != null && !isBefore(sliceDate, marketLocation.marketingEnd)) ||
    readOnly;
  const isFifteenMinuteResolution = currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES;

  const correctedDSTColumn = rowSlices.findIndex(
    (dstSlice, dstIndex) =>
      dstIndex - 1 >= 0 &&
      rowSlices[dstIndex - 1].date.getDate() === dstSlice.date.getDate() &&
      rowSlices[dstIndex - 1].date.getTimezoneOffset() !== dstSlice.date.getTimezoneOffset(),
  );
  const isFutureFromVPP = (sliceDate: Date) =>
    currentForecastProperty?.target !== ForecastTarget.VPP &&
    marketLocation.forecastProperties?.some(
      (prop) =>
        prop.target === ForecastTarget.VPP &&
        isWithinInterval(sliceDate, { start: prop.validFrom, end: prop.validTo ?? addDays(sliceDate, 1) }),
    );

  const futureOptimizedMessage = () => {
    const isOptimized = marketLocation.forecastProperties?.some(
      (prop) => prop.target === ForecastTarget.VPP && prop.editableMarkets.length === 0,
    );
    if (isOptimized) return t('MARKET_LOCATION_FORECAST_TABLE_FUTURE_INTRADAY_OPTIMIZATION_MESSAGE');
    let marketingStartDate: Maybe<string> | undefined;
    marketingStartDate = marketLocation.forecastProperties?.find(
      (prop) => prop.target === ForecastTarget.VPP,
    )?.validFrom;
    return t('MARKET_LOCATION_FORECAST_TABLE_FUTURE_INTRADAY_MARKETING_MESSAGE', {
      startDate: marketingStartDate && format(marketingStartDate, 'dd.MM.yyyy'),
    });
  };

  return (
    <tr
      className={clsx('even:bg-grey-50', {
        'cursor-cell':
          !readOnly &&
          deadlineDate &&
          isBefore(deadlineDate, new Date(interval.end).setHours(date.getHours(), date.getMinutes())),
      })}
    >
      <th
        className={clsx(
          'min-w-40 box-border border-0 border-b border-solid pl-6 text-start text-sm font-normal leading-4',
          row % 4 === 3 ? 'border-blue-grey-100' : 'border-grey-100',
        )}
        colSpan={2}
        onMouseDown={selectRow(row)}
        onMouseMove={expandRowSelection(row)}
      >
        {`${date.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
        })} - ${addMinutes(date, isFifteenMinuteResolution ? 15 : 60).toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: date.getHours() < 12 ? 'h23' : 'h24',
        })}`}
      </th>
      {rowSlices.map((slice, column) => (
        <Fragment key={`slice-${device.id}-${slice.id}`}>
          {
            // End of summertime
            column + 1 < rowSlices.length &&
            slice.date.getDate() === rowSlices[column + 1].date.getDate() &&
            slice.date.getTimezoneOffset() !== rowSlices[column + 1].date.getTimezoneOffset() ? (
              <DSTMultiCell
                sliceValues={dstSliceValues?.get(device.id)?.at(row % 4)}
                firstSliceDate={slice.date}
                showSpotPrices={showSpotPrices}
                spotPrice={spotPrice}
                latestHistoricalSpotPrice={spotPriceForecast?.latestHistoricalSpotPrice as Date | null | undefined}
                cellIndex={{ row, column }}
                sliceIDs={[slice.id, rowSlices[column + 1].id]}
                editedSlices={editedSlices}
                toggleModal={isReadOnly(slice.date) ? undefined : toggleDSTModal}
              />
            ) : column - 1 >= 0 &&
              rowSlices[column - 1].date.getDate() === slice.date.getDate() &&
              rowSlices[column - 1].date.getTimezoneOffset() !== slice.date.getTimezoneOffset() ? (
              <></>
            ) : // Normal time
            slice.id !== DST_CORRECTION_ID ? (
              <DecoratedCell
                device={device}
                deadlineDate={deadlineDate}
                slice={slice}
                powerLevelRules={powerLevelRules}
                rules={rules}
                isDstSlice={rowSlices[column].id === dstSliceID}
                spotPrice={showSpotPrices ? spotPrice(slice.date) : undefined}
                latestHistoricalSpotPrice={spotPriceForecast?.latestHistoricalSpotPrice as Date | null | undefined}
                cellIndex={{
                  row: row,
                  column: correctedDSTColumn !== -1 && correctedDSTColumn < column ? column - 1 : column,
                }}
                readOnly={isReadOnly(slice.date) || isFutureFromVPP(slice.date)}
                resetTrigger={resetTrigger}
                editedSlices={editedSlices}
                isHighlighted={isHighlighted}
                overviewMap={overviewMap}
                selectHandle={selectHandle}
                selectedCells={selectedCells}
                setRevokeDowntimeInterval={setRevokeDowntimeInterval}
                setEditedSlices={setEditedSlices}
                setErroneousSlices={setErroneousSlices}
                setIsHighlighted={setIsHighlighted}
                setOverviewMap={setOverviewMap}
                switchFocus={switchFocus}
                setSelectHandle={setSelectHandle}
                setSelectedCells={setSelectedCells}
                handleSelectAll={handleSelectAll}
                pasteCopiedCells={pasteCopiedCells}
                saveChanges={saveChanges}
                currentForecastProperty={currentForecastProperty}
                isFutureIntraday={isFutureFromVPP(slice.date)}
                futureIntradayMessage={futureOptimizedMessage()}
              />
            ) : (
              // An empty cell for DST shenanigans
              <td
                className={clsx(
                  'group/cell relative border-0 border-b border-solid p-0 text-center',
                  row % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
                  { 'bg-blue-grey-200/5': isWeekend(slice.date) },
                )}
              >
                <input
                  ref={setDstRef}
                  type="button"
                  value="–"
                  disabled={deadlineDate && isBefore(slice.date, deadlineDate)}
                  className="absolute inset-0 box-border w-full border-0 border-l border-solid border-grey-100 bg-transparent"
                  onClick={() => switchFocus(row, column, Direction.Up)}
                />
                <Popover baseElement={dstRef} className="group-hover/cell:opacity-100" info={t('FORECAST_DST_SKIP')} />
              </td>
            )
          }
          {column === rowSlices.length - 1 && (
            <td
              className={clsx(
                'border-0 border-b border-solid border-divider',
                row % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
                { 'bg-blue-grey-200/5': isWeekend(slice.date) },
              )}
            />
          )}
        </Fragment>
      ))}
    </tr>
  );
}

export const DeviceTableRow = memo(DeviceTableRowMemo, equals);
